import { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { EVENT_COLORS, ROLE_LABEL, canApproveType, effectiveRoles } from "../lib/permissions";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Check, X, ClipboardCheck } from "lucide-react";

export default function ApprovalsView({ pending, user, onRefresh }) {
  const [reason, setReason] = useState({});

  const approve = async (id) => {
    try {
      await api.post(`/events/${id}/approve`, { feedback: "" });
      toast.success("Event approved");
      onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Approval failed"); }
  };
  const reject = async (id) => {
    try {
      await api.post(`/events/${id}/reject`, { feedback: reason[id] || "" });
      toast.success("Event rejected");
      onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Rejection failed"); }
  };

  if (pending.length === 0) {
    return (
      <Card className="glass-card p-10 text-center">
        <ClipboardCheck className="w-10 h-10 text-amber-400 mx-auto mb-3" />
        <h3 className="font-heading text-xl mb-1">All clear</h3>
        <p className="text-slate-500">No events awaiting your approval.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl text-[var(--sn-gold)]">Executive Authorization Desk</h2>
        <Badge className="bg-rose-950 text-rose-300 border-rose-700">{pending.length} pending</Badge>
      </div>
      {pending.map(e => {
        const color = EVENT_COLORS[e.event_type];
        const canAct = canApproveType(effectiveRoles(user), e.event_type);
        return (
          <Card key={e.id} className="glass-card p-5" data-testid={`approval-card-${e.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={color?.chip}>{e.event_type}</Badge>
                  <span className="text-xs text-slate-500 font-mono">{e.date} · {e.start_time}</span>
                </div>
                <h3 className="font-heading text-lg">{e.title}</h3>
                {e.description && (
                  <p className="text-sm text-slate-300 mt-1 whitespace-pre-line">{e.description}</p>
                )}
                <p className="text-sm text-slate-400">{e.address}</p>
                {e.event_type === "Candidate Event" && (
                  <p className="text-xs text-sky-300 mt-1">
                    {e.duration_hours}h requested · for {(e.target_user_ids || []).length} candidate(s) · 20h weekly budget applies
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  Submitted by <span className="text-slate-300">{e.created_by_name}</span> · {ROLE_LABEL[e.created_by_role]}
                </p>
                {e.required_forms && (
                  <p className="text-xs text-emerald-400 mt-1">Forms: {e.form_proof}</p>
                )}
              </div>
              {canAct ? (
                <div className="flex flex-col gap-2 min-w-[220px]">
                  <Textarea rows={2} placeholder="Rejection feedback (optional)"
                    data-testid={`reject-reason-${e.id}`}
                    value={reason[e.id] || ""}
                    onChange={(ev) => setReason({ ...reason, [e.id]: ev.target.value })}
                    className="bg-slate-900 border-slate-700 text-sm" />
                  <div className="flex gap-2">
                    <Button data-testid={`approve-event-button-${e.id}`} onClick={() => approve(e.id)}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white gap-1">
                      <Check className="w-4 h-4" /> Approve
                    </Button>
                    <Button data-testid={`reject-event-button-${e.id}`} onClick={() => reject(e.id)}
                      variant="outline" className="flex-1 border-rose-800 text-rose-300 hover:bg-rose-950 gap-1">
                      <X className="w-4 h-4" /> Reject
                    </Button>
                  </div>
                </div>
              ) : (
                <span className="text-xs text-slate-500">Awaiting {ROLE_LABEL[e.approver_role]}</span>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
